import { useState } from "react";
import { VerifiedBadge } from "@/components/moderation/VerifiedBadge";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MyCompetition } from "@/pages/Tournaments";
import { Pill } from "@/components/competition/shared";

export function LeagueCard({ lg }) {
  return (
    <Link to={`/l/${lg.league_id}`} className="panel group block p-4 transition-colors duration-200 hover:border-amber-500/60" data-testid={`league-card-${lg.league_id}`}>
      <div className="flex items-start justify-between gap-2"><h3 className="font-heading text-lg text-amber-100 group-hover:text-aether-300">{lg.name} <VerifiedBadge kind="league" verified={lg.verified} compact /></h3><Pill value={lg.enrollment} /></div>
      <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-copper-300">{lg.community_name}</p>
      <p className="mt-2 line-clamp-2 text-sm text-slate-400">{lg.description || "No description yet."}</p>
      <p className="mt-2 text-xs text-slate-500"><Trophy className="mr-1 inline h-3.5 w-3.5" />{lg.match_kind === "pod" ? "Multiplayer pods" : lg.match_kind === "team" ? `${lg.pairing?.team_size || 2}v${lg.pairing?.team_size || 2} team matches` : `1v1, best of ${lg.best_of}`}{lg.active_season ? ` · ${lg.active_season.name} running` : ""}</p>
    </Link>
  );
}

export default function Leagues() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [enrollment, setEnrollment] = useState("");
  const params = { q: q || undefined, enrollment: enrollment || undefined };
  const { data, isLoading } = useQuery({ queryKey: ["leagues", params], queryFn: () => api.get("/leagues", { params }).then((r) => r.data) });
  return (
    <div className="container-x py-12" data-testid="leagues-page">
      <p className="eyebrow">Leagues</p>
      <h1 className="font-display text-4xl text-amber-100 sm:text-5xl">Leagues of the Foundry</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">Season-long competition inside approved communities, with divisions, standings and a permanent record of every closed season.</p>
      {user && <MyCompetition />}
      <div className="mt-10 flex flex-wrap gap-3">
        <label className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input className="field pl-9" placeholder="Search leagues" value={q} onChange={(e) => setQ(e.target.value)} data-testid="leagues-search-input" /></label>
        <select className="field w-48" value={enrollment} onChange={(e) => setEnrollment(e.target.value)} data-testid="leagues-enrollment-filter">
          <option value="">Any enrollment</option><option value="open">Open</option><option value="application">By application</option><option value="invite_only">Invite only</option></select>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="leagues-directory">
        {isLoading ? <p className="text-slate-500">Loading…</p> : data?.items.length ? data.items.map((lg) => <LeagueCard key={lg.league_id} lg={lg} />)
          : <p className="text-slate-500" data-testid="leagues-empty">No leagues match yet.</p>}
      </div>
    </div>
  );
}
